import { RUN_TIMEOUT_MS } from '@/pages/scripts/hooks/notebook-bridge-client'

import { bridgeFailure, invalidInput, openNotebookBridge } from './execute-live-notebook'

/** Matches `MAX_RUN_CELLS` in the AI service's `runCells.ts` (the bridge itself takes up to 50). */
export const MAX_RUN_CELLS = 20

interface RunResult {
  cells?: unknown[]
  failed_index?: number | null
  timed_out?: boolean
  running?: number[]
}

function readIndices(raw: unknown): number[] | string {
  if (!Array.isArray(raw) || raw.length === 0) return '`indices` must be a non-empty list of cell indices.'
  if (raw.length > MAX_RUN_CELLS) return `At most ${MAX_RUN_CELLS} cells in one run_cells call.`
  if (raw.some(i => typeof i !== 'number' || !Number.isInteger(i) || i < 0)) {
    return '`indices` must be whole numbers, 0 or more (0-based).'
  }

  if (new Set(raw).size !== raw.length) return '`indices` must not repeat a cell.'

  return raw as number[]
}

/**
 * `run_cells`: runs cells of the notebook open in a live session in the user's kernel, through the
 * JupyterLab bridge (in order, stopping at the first error, up to 120 s on the bridge's side). The
 * server lets the call through only after its checks (the caller's role, the open notebook and run consent).
 */
export async function executeRunCells(input: unknown): Promise<unknown> {
  const open = openNotebookBridge(input)
  if ('success' in open) return open
  const { notebookId, bridge } = open

  const indices = readIndices((input as { indices?: unknown }).indices)
  if (typeof indices === 'string') return invalidInput(indices, notebookId)

  try {
    const result = await bridge.request<RunResult>('run', { indices }, RUN_TIMEOUT_MS)

    return {
      success: true,
      notebook_id: notebookId,
      cells: Array.isArray(result?.cells) ? result.cells : [],
      failed_index: result?.failed_index ?? null,
      ...(result?.timed_out ? { timed_out: true, running: result.running ?? [] } : {}),
    }
  } catch (err) {
    return bridgeFailure(notebookId, err)
  }
}
